import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";

import {
  getOppositionFileOpposition,
  saveOppositionFileOpposition,
  generateOppositionFileOppositionPresignedUrls,
  type FileOppositionData,
} from "../../api/opposition";

export const OPPOSITION_FILE_OPPOSITION_FORM_ID = "opposition-file-opposition-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const INPUT_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const INPUT_ERROR_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-red-500 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-red-500 box-border";
const READONLY_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px] box-border cursor-default select-text";
const REQ = <span className="text-red-500 ml-0.5">*</span>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseDateStr(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toISODate(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

// ---------------------------------------------------------------------------
// Local row type
// ---------------------------------------------------------------------------

type MarkRow = {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfOpposition: Date | null;
  oppositionNo: string;
  pdfFileKey: string | null;
  pdfFilename: string | null;
  pdfDownloadUrl: string | null;
  pendingPdfFile: File | null;
  localPdfPreviewUrl: string | null;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OppositionFileOppositionView({ leadId, onSuccess }: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);

  const [data, setData] = useState<FileOppositionData | null>(null);
  const [rows, setRows] = useState<MarkRow[]>([]);

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Set<string>>(new Set());

  const hasError = (key: string) => errors.has(key);
  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!leadId || loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;
    setLoading(true);

    getOppositionFileOpposition(leadId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setRows(
          d.conflictingMarks.map((cm) => ({
            id: cm.id,
            conflictingMark: cm.conflictingMark,
            applicationNo: cm.applicationNo,
            dateOfOpposition: parseDateStr(cm.dateOfOpposition),
            oppositionNo: cm.oppositionNo ?? "",
            pdfFileKey: cm.oppositionPdfFileKey,
            pdfFilename: cm.oppositionPdfFilename,
            pdfDownloadUrl: cm.oppositionPdfDownloadUrl,
            pendingPdfFile: null,
            localPdfPreviewUrl: null,
          }))
        );
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : "Failed to load file opposition data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      rows.forEach((r) => {
        if (r.localPdfPreviewUrl) URL.revokeObjectURL(r.localPdfPreviewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Row update helpers
  // ---------------------------------------------------------------------------

  const updateRow = (id: string, updates: Partial<MarkRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  };

  const handlePdfFileSelected = (id: string, file: File) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        if (r.localPdfPreviewUrl) URL.revokeObjectURL(r.localPdfPreviewUrl);
        return { ...r, pendingPdfFile: file, localPdfPreviewUrl: URL.createObjectURL(file) };
      })
    );
  };

  const handleRemovePdf = (id: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        if (r.localPdfPreviewUrl) URL.revokeObjectURL(r.localPdfPreviewUrl);
        return {
          ...r,
          pendingPdfFile: null,
          localPdfPreviewUrl: null,
          pdfFileKey: null,
          pdfFilename: null,
          pdfDownloadUrl: null,
        };
      })
    );
  };

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const validate = (): Set<string> => {
    const next = new Set<string>();
    rows.forEach((r) => {
      if (!r.dateOfOpposition) next.add(`${r.id}-date`);
      if (!r.oppositionNo.trim()) next.add(`${r.id}-no`);
      if (!r.pendingPdfFile && !r.pdfFileKey) next.add(`${r.id}-pdf`);
    });
    return next;
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    const validationErrors = validate();
    if (validationErrors.size > 0) {
      setErrors(validationErrors);
      toast.error("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    try {
      // 1. Batch upload all pending PDFs
      type PendingUpload = { rowId: string; file: File };
      const uploads: PendingUpload[] = rows
        .filter((r) => r.pendingPdfFile)
        .map((r) => ({ rowId: r.id, file: r.pendingPdfFile! }));

      const keyMap: Record<string, string> = {};
      if (uploads.length > 0) {
        const presigned = await generateOppositionFileOppositionPresignedUrls(
          leadId,
          uploads.map((u) => ({
            filename: u.file.name,
            mimeType: u.file.type || "application/octet-stream",
          }))
        );
        if (presigned.length !== uploads.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          uploads.map(async (u, i) => {
            const target = presigned[i];
            const res = await fetch(target.url, {
              method: "PUT",
              body: u.file,
              headers: { "Content-Type": u.file.type || "application/octet-stream" },
            });
            if (!res.ok) throw new Error(`Failed to upload ${u.file.name}`);
            keyMap[u.rowId] = target.key;
          })
        );
      }

      // 2. Build and send payload
      const saved = await saveOppositionFileOpposition(leadId, {
        conflictingMarks: rows.map((r) => {
          const uploadedKey = keyMap[r.id] ?? null;
          return {
            conflictingMarkId: r.id,
            dateOfOpposition: toISODate(r.dateOfOpposition),
            oppositionNo: r.oppositionNo.trim() || null,
            oppositionPdfFileKey: uploadedKey ?? r.pdfFileKey,
            oppositionPdfFilename: uploadedKey
              ? (r.pendingPdfFile?.name ?? null)
              : r.pdfFilename,
          };
        }),
      });

      // 3. Sync state from saved response
      setData(saved);
      setRows(
        saved.conflictingMarks.map((cm) => ({
          id: cm.id,
          conflictingMark: cm.conflictingMark,
          applicationNo: cm.applicationNo,
          dateOfOpposition: parseDateStr(cm.dateOfOpposition),
          oppositionNo: cm.oppositionNo ?? "",
          pdfFileKey: cm.oppositionPdfFileKey,
          pdfFilename: cm.oppositionPdfFilename,
          pdfDownloadUrl: cm.oppositionPdfDownloadUrl,
          pendingPdfFile: null,
          localPdfPreviewUrl: null,
        }))
      );

      toast.success("File Opposition saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save file opposition");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Early returns
  // ---------------------------------------------------------------------------

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  const isMultiApp = rows.length > 1;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <form
        id={OPPOSITION_FILE_OPPOSITION_FORM_ID}
        onSubmit={handleSubmit}
        className="flex flex-col gap-6 w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {rows.length === 0 ? (
          <p className="text-sm text-[#6D6D6D]">No eligible applications found for filing.</p>
        ) : (
          rows.map((row, idx) => {
            const inputPdfId = `opp-file-pdf-${row.id}`;
            const hasPdf = !!(row.pendingPdfFile ?? row.pdfFileKey);
            const pdfDisplayName = row.pendingPdfFile?.name ?? row.pdfFilename ?? null;
            const pdfPreviewUrl = row.localPdfPreviewUrl ?? row.pdfDownloadUrl ?? null;
            const isDragging = draggingId === row.id;

            return (
              <div
                key={row.id}
                className={`flex flex-col gap-4 ${
                  isMultiApp ? "p-4 rounded-[8px] border border-[#EEEEEE]" : ""
                }`}
              >
                {isMultiApp && (
                  <h3 className="text-[13px] font-semibold text-[#222]">
                    {row.conflictingMark ?? ""}
                    {row.applicationNo ? ` (${row.applicationNo})` : ""}
                  </h3>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 21z — Date of Opposition */}
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_CLASS}>Date of Opposition{REQ}</label>
                    <DateTimePicker
                      value={row.dateOfOpposition}
                      onChange={(d) => {
                        updateRow(row.id, { dateOfOpposition: d });
                        clearError(`${row.id}-date`);
                      }}
                      placeholder="Select Date"
                      buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
                      error={hasError(`${row.id}-date`)}
                    />
                  </div>

                  {/* 21za — Opposition No. */}
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_CLASS}>Opposition No.{REQ}</label>
                    <input
                      type="text"
                      className={hasError(`${row.id}-no`) ? INPUT_ERROR_CLASS : INPUT_CLASS}
                      placeholder="Text Here"
                      value={row.oppositionNo}
                      onChange={(e) => {
                        updateRow(row.id, { oppositionNo: e.target.value });
                        clearError(`${row.id}-no`);
                      }}
                    />
                  </div>


                </div>

                {/* Opposition PDF upload */}
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>Opposition PDF{REQ}</label>
                  {hasPdf && pdfDisplayName ? (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
                      <span className="text-xs text-[#222] truncate flex-1">{pdfDisplayName}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {pdfPreviewUrl && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() =>
                              setPreview({
                                url: pdfPreviewUrl,
                                name: pdfDisplayName,
                                kind: detectPreviewKind(pdfDisplayName),
                                revokeOnClose: !!row.localPdfPreviewUrl,
                              })
                            }
                            className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-50"
                          >
                            Preview
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => document.getElementById(inputPdfId)?.click()}
                          className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-50"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleRemovePdf(row.id)}
                          aria-label="Remove PDF"
                          className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            className="w-4 h-4"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-5 py-4 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
                        isDragging
                          ? "border-[#222] bg-[#F5F5F5]"
                          : hasError(`${row.id}-pdf`)
                            ? "border-red-500"
                            : "border-[#D8DAEB]"
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDraggingId(row.id);
                      }}
                      onDragLeave={() => setDraggingId(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDraggingId(null);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          handlePdfFileSelected(row.id, file);
                          clearError(`${row.id}-pdf`);
                        }
                      }}
                      onClick={() => document.getElementById(inputPdfId)?.click()}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <img
                          src={uploadDropzoneGraphic}
                          alt=""
                          width={40}
                          height={40}
                          className="flex-shrink-0"
                        />
                        <div className="flex flex-col gap-1">
                          <p className="text-[13px] font-medium text-[#101111]">
                            Upload Opposition PDF
                          </p>
                          <p className="text-[11px] text-[#7D7D7D]">PDF format, up to 50MB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={(e) => {
                          e.stopPropagation();
                          document.getElementById(inputPdfId)?.click();
                        }}
                        className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[13px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 self-start sm:self-auto"
                      >
                        Select File
                      </button>
                    </div>
                  )}
                  <input
                    id={inputPdfId}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handlePdfFileSelected(row.id, file);
                        clearError(`${row.id}-pdf`);
                      }
                      e.currentTarget.value = "";
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </form>

      <FilePreviewModal
        open={!!preview}
        onClose={() => {
          if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
          setPreview(null);
        }}
        url={preview?.url ?? ""}
        name={preview?.name ?? ""}
        kind={preview?.kind ?? "pdf"}
      />
    </>
  );
}
